import React, { useEffect, useState } from "react";
import { Alert, Form, Input, Modal, Skeleton, Typography } from "antd";
import * as dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import { getExactOrderConfirmationRecipient, sendExactOrderConfirmation } from "functions/api/exact";

const { Text } = Typography;

/**
 * "Bevestiging sturen" voor een Exact-order.
 *
 * Waarom een dialoog en niet gewoon een knop: hiermee gaat er post naar een
 * klant, en dat is niet terug te draaien. Je ziet dus eerst om welke order het
 * gaat, naar welk adres hij gaat, en of er al eens een is verstuurd.
 *
 * Het adres wordt voorgesteld door de API (webshopklant, anders het
 * Exact-account) maar is aanpasbaar — "stuur hem even naar mijn collega" is een
 * normale vraag, en een order zonder bekend adres zou anders niet te
 * beantwoorden zijn.
 *
 * Of er al eerder een bevestiging uitging komt uit datzelfde API-antwoord en
 * niet uit een Firestore-listener: zo werkt de waarschuwing overal waar deze
 * dialoog opengaat, zonder dat het CMS de collectie hoeft te kunnen lezen.
 */
const SendConfirmationModal = ({ order, open, onClose, onSent }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [recipient, setRecipient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !order?.id) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setRecipient(null);
    form.resetFields();

    getExactOrderConfirmationRecipient(order.id)
      .then((data) => {
        if (cancelled) return;
        setRecipient(data);
        form.setFieldsValue({ email: data.email || "" });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Ontvanger van de bevestiging niet op te halen:", err);
        setError(t("exact.confirmation.recipient_failed"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, order?.id, form, t]);

  const handleSend = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setIsSending(true);
    setError(null);
    try {
      const result = await sendExactOrderConfirmation(order.id, values.email);
      onSent?.(result);
      onClose();
    } catch (err) {
      console.error("Versturen bevestiging mislukt:", err);
      setError(err?.response?.data?.message || t("exact.confirmation.send_failed"));
    } finally {
      setIsSending(false);
    }
  };

  const orderLabel = order?.orderNumber || order?.yourRef || "";

  return (
    <Modal
      open={open}
      title={t("exact.confirmation.title")}
      okText={t("exact.confirmation.send")}
      cancelText={t("cancel")}
      confirmLoading={isSending}
      onOk={handleSend}
      onCancel={onClose}
      okButtonProps={{ disabled: isLoading }}
      destroyOnClose
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        <>
          <p style={{ marginBottom: 16 }}>
            {t("exact.confirmation.intro", {
              order: orderLabel,
              customer: recipient?.name || order?.customerName || "-",
            })}
          </p>

          {recipient?.lastSent?.lastSentAt ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t("exact.confirmation.already_sent", {
                date: dayjs.unix(recipient.lastSent.lastSentAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm),
                email: recipient.lastSent.lastSentTo,
              })}
            />
          ) : null}

          {recipient && !recipient.email ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t("exact.confirmation.no_known_email")}
            />
          ) : null}

          <Form form={form} layout="vertical" requiredMark={false}>
            <Form.Item
              name="email"
              label={t("exact.confirmation.recipient")}
              rules={[
                { required: true, message: t("exact.confirmation.email_required") },
                { type: "email", message: t("exact.confirmation.email_invalid") },
              ]}
            >
              <Input placeholder="naam@bedrijf.nl" autoComplete="off" />
            </Form.Item>
          </Form>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("exact.confirmation.explainer")}
          </Text>

          {error ? <Alert type="error" showIcon style={{ marginTop: 16 }} message={error} /> : null}
        </>
      )}
    </Modal>
  );
};

export default SendConfirmationModal;
